import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import shield from 'assets/img/icons/shield.png';
import { ProductContext } from 'context/Context';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import y from 'y4/Utils';
import useOrderHook from 'y4/hooks/useOrderHook';

const PaymentMethod = ({ payableTotal, shippingCost, subTotal, total }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('commerce');
  const {
    productsState: {
      config: {
        paymentMethods,
        buyerProtectionItems,
        buyerProtection,
        termsConditions,
        currency
      },
      paymentMethod
    },
    productsDispatch
  } = useContext(ProductContext);

  const { confirmPay, loadPoints } = useOrderHook();

  const [point, setPoint] = useState({ points: 0, amount: 0 });

  useEffect(() => {
    loadPoints().then(rt => {
      if (rt.isOk()) {
        setPoint(rt.data);
      } else {
        y.toastError(rt.message);
      }
    });
  }, []);

  const methodChanged = method => {
    productsDispatch({
      type: 'CHANGE_PAYMENT_METHOD',
      payload: { paymentMethod: method }
    });
  };

  const onBridgeReady = (prepay, success) => {
    window.WeixinJSBridge.invoke(
      'getBrandWCPayRequest',
      prepay,
      function (res) {
        if (res.err_msg == 'get_brand_wcpay_request:ok') {
          success();
        }
      }
    );
  };

  const weixinPay = (prepay, success) => {
    if (typeof WeixinJSBridge == 'undefined') {
      if (document.addEventListener) {
        document.addEventListener(
          'WeixinJSBridgeReady',
          () => {
            onBridgeReady(prepay, success);
          },
          false
        );
      } else if (document.attachEvent) {
        document.attachEvent('WeixinJSBridgeReady', () => {
          onBridgeReady(prepay, success);
        });

        document.attachEvent('onWeixinJSBridgeReady', () => {
          onBridgeReady(prepay, success);
        });
      }
    } else {
      onBridgeReady(prepay, success);
    }
  };

  const onSubmit = () => {
    if (!paymentMethod) {
      y.toastError('No payment method');
      return;
    }

    confirmPay({
      payableTotal,
      shippingCost,
      subTotal,
      total,
      points: point.points,
      pointsAmount: point.amount
    }).then(rt => {
      if (rt.isOk()) {
        //Clear  cartItems: and promo
        productsDispatch({
          type: 'CHECKOUT'
        });
        //Show message
        toast(
          <div className="text-700">
            <h5 className="text-success fs-0 mb-0">
              {rt.data.title ? (
                rt.data.title
              ) : (
                <Trans ns="commerce">Payment success!</Trans>
              )}
            </h5>
            <hr className="my-2" />
            {rt.data.message ? <p>{rt.data.message}</p> : null}
            <Trans ns="commerce">Total</Trans>:{' '}
            <strong>
              {currency}
              {payableTotal}
            </strong>
            <br />
            <Trans ns="commerce">Payment method</Trans>:{' '}
            <strong className="text-capitalize">{paymentMethod.title}</strong>
          </div>
        );
        if (rt.data.weixin) {
          weixinPay(rt.data.weixin, () => {
            navigate(rt.data.to);
          });
        } else {
          navigate(rt.data.to);
        }
      } else {
        y.toastError(rt.message);
      }
    });
  };

  return (
    <Card>
      <Card.Header className="bg-light">
        <h5 className="mb-0">
          <Trans ns="commerce">Payment Method</Trans>
        </h5>
      </Card.Header>
      <Card.Body>
        {paymentMethods.map((item, index) => (
          <div key={index}>
            <Form.Check
              type="radio"
              id="credit-card"
              className="mb-0 form-check"
            >
              <Form.Check.Input
                type="radio"
                onChange={() => methodChanged(item)}
                name="payment-method"
                defaultChecked={index === 0}
              />
              <Form.Check.Label className="mb-2 fs-1">
                {item.title}
              </Form.Check.Label>
            </Form.Check>
            <Form className="ms-4">
              <p>
                {y.formatByNamedArgs(item.description, {
                  POINTS: point.points,
                  POINTS_AMOUNT: point.amount
                })}
              </p>
            </Form>
          </div>
        ))}
        <div className="border-dashed border-bottom my-5"></div>
        <Row>
          <Col
            md={7}
            xl={12}
            xxl={7}
            className="px-md-3 mb-xxl-0 position-relative"
          >
            <div className="d-flex">
              <img
                src={shield}
                alt="protection"
                width="60"
                height="60"
                className="me-3"
              />
              <div className="flex-1">
                <h5 className="mb-2">
                  <Trans ns="commerce">Buyer Protection</Trans>
                </h5>
                {buyerProtectionItems.map((item, index) => (
                  <Form.Check
                    key={index}
                    id="full-refund"
                    className="mb-0 form-check"
                  >
                    <Form.Check.Input
                      className="mb-0"
                      type="checkbox"
                      defaultChecked
                    />
                    <Form.Check.Label className="mb-0">
                      <span dangerouslySetInnerHTML={{ __html: item }}></span>
                    </Form.Check.Label>
                  </Form.Check>
                ))}
                <Link
                  onClick={() => {
                    y.modal.alter(t('Buyer Protection'), null, buyerProtection);
                  }}
                  className="fs--1 ms-3 ps-2"
                >
                  <Trans ns="commerce">Learn More</Trans>
                  <FontAwesomeIcon
                    icon="caret-right"
                    className="ms-1"
                    transform="down-2"
                  />
                </Link>
              </div>
            </div>
            <div className="vertical-line d-none d-md-block d-xl-none d-xxl-block"></div>
          </Col>
          <Col
            md={5}
            xl={12}
            xxl={5}
            className="ps-xxl-5 text-center text-md-start text-xl-center text-xxl-start"
          >
            <div className="border-dashed border-bottom d-block d-md-none d-xl-block d-xxl-none my-4"></div>
            <div className="fs-2 fw-semi-bold">
              <Trans ns="commerce">All Total</Trans>:{' '}
              <span className="text-primary">
                {currency}
                {payableTotal}
              </span>
            </div>
            <Button
              variant="success"
              className="mt-3 px-5"
              type="submit"
              disabled={payableTotal === '0.00'}
              onClick={onSubmit}
            >
              <Trans ns="commerce">Confirm &amp; Pay</Trans>
            </Button>
            <p className="fs--1 mt-3 mb-0">
              <Trans ns="commerce">
                By clicking <strong>Confirm &amp; Pay </strong> button you agree
                to the
              </Trans>
              <Link
                onClick={() => {
                  y.modal.alter(t('Terms & Conditions'), null, termsConditions);
                }}
              >
                <Trans ns="commerce">Terms &amp; Conditions</Trans>
              </Link>
            </p>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

PaymentMethod.propTypes = {
  payableTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  shippingCost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default PaymentMethod;
